import { useEffect, useState } from "react";
import apiWithRefresh from "../utils/apiWithRefresh";
import useDarkMode from "./useDarkMode";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Typography,
  IconButton,
  Stack,
  Select,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { Helmet } from "react-helmet-async";
import FeesTypeEnum from "../utils/FeesTypeEnum";
import { useToast } from "../utils/useToast";
import GlobalToast from "../components/GlobalToast";

const initialForm = {
  id: null,
  name: "",
  type: "",
  amount: "0.00",
  currency: "USD",
  description: "",
};

const AdminPrices = () => {
  useDarkMode();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  // Fix: Define openDeleteConfirm and handleDelete
  const openDeleteConfirm = (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const handleDelete = async () => {
    try {
      await apiWithRefresh.delete(`config/${deleteId}`);
      showToast("Config deleted successfully!", "success");
      setConfirmOpen(false);
      setDeleteId(null);
      fetchConfigs();
    } catch (err) {
      showToast(err.message, "error");
    }
  };
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, showToast] = useToast();

  const fetchConfigs = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiWithRefresh.get("config");
      setConfigs(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  const openAddModal = () => {
    setForm(initialForm);
    setEditingId(null);
    setModalOpen(true);
  };

  const openEditModal = async (item) => {
    try {
      const latest = await apiWithRefresh.get(`config/${item.id}`);
      setForm({
        id: latest.id,
        name: latest.name || "",
        type: latest.type,
        amount:
          typeof latest.amount === "number"
            ? latest.amount.toFixed(2)
            : parseFloat(latest.amount).toFixed(2) || "0.00",
        currency: latest.currency ?? "USD",
        description: latest.description || "",
      });
      setEditingId(latest.id);
      setModalOpen(true);
    } catch (err) {
      showToast("Failed to fetch config details.", "error");
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingId(null);
    setForm(initialForm);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleAmountCurrencyChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({
      ...f,
      [name]:
        name === "amount"
          ? value === ""
            ? "0.00"
            : parseFloat(value).toFixed(2)
          : value,
    }));
  };

  const handleAddOrUpdate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      showToast("Name is required.", "warning");
      return;
    }
    try {
      const payload = {
        name: form.name,
        type: form.type,
        amount:
          typeof form.amount === "number"
            ? form.amount.toFixed(2)
            : parseFloat(form.amount).toFixed(2) || "0.00",
        currency: form.currency,
        description: form.description,
      };
      if (editingId) {
        await apiWithRefresh.put(`config/${editingId}`, payload);
        showToast("Config updated successfully!", "success");
      } else {
        await apiWithRefresh.post("config", payload);
        showToast("Config added successfully!", "success");
      }
      setModalOpen(false);
      setEditingId(null);
      setForm(initialForm);
      fetchConfigs();
    } catch (err) {
      showToast(err.message, "error");
    }
  };

  // DataGrid columns
  const columns = [
    { field: "name", headerName: "Name", flex: 1 },
    { field: "amount", headerName: "Amount", flex: 1 },
    { field: "currency", headerName: "Currency", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <IconButton
            color="primary"
            onClick={() => openEditModal(params.row)}
            aria-label={`Update config ${params.row.name}`}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            color="error"
            onClick={() => openDeleteConfirm(params.row.id)}
            aria-label={`Delete config ${params.row.name}`}
          >
            <DeleteIcon />
          </IconButton>
        </Stack>
      ),
      flex: 1,
    },
  ];

  return (
    <>
      <Helmet>
        <title>AKAAL KIDS - Admin Panel | Our Prices</title>
      </Helmet>
      <Box
        sx={{
          mt: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
          Our Prices
        </Typography>
        {loading && <Typography>Loading...</Typography>}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {!loading && !error && (
          <Box sx={{ width: "100%", maxWidth: 900 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
               
              onClick={openAddModal}
              sx={{ mb: 2 }}
            >
              Add New
            </Button>
            <DataGrid
              autoHeight
              rows={configs.map((c) => ({ ...c, id: c.id }))}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 20, 50]}
              disableSelectionOnClick
            />
          </Box>
        )}
        {/* Add/Edit Dialog */}
        <Dialog
          open={modalOpen}
          onClose={handleModalClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {editingId ? "Edit Price Config" : "Add New Price Config"}
          </DialogTitle>
          <form onSubmit={handleAddOrUpdate}>
            <DialogContent>
              <TextField
                label="Name"
                name="name"
                value={form.name}
                onChange={handleChange}
                fullWidth
                required
                margin="normal"
              />
              <Select
                label="Type"
                name="type"
                value={form.type}
                onChange={handleChange}
                fullWidth
                required
                sx={{ mb: 2 }}
                displayEmpty
              >
                <MenuItem value="">Select Type</MenuItem>
                {Object.entries(FeesTypeEnum).map(([key, value]) => (
                  <MenuItem key={value} value={value}>
                    {key
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (c) => c.toUpperCase())}
                  </MenuItem>
                ))}
              </Select>
              <TextField
                label="Amount"
                name="amount"
                type="number"
                slotProps={{ input: { min: 1, step: 0.01 } }}
                sx={{ mb: 2 }}
                value={form.amount}
                onChange={(e) => {
                  const val = e.target.value;
                  setForm((f) => ({
                    ...f,
                    amount: val === "" ? "0.00" : Number(val).toFixed(2),
                  }));
                }}
                fullWidth
                margin="normal"
                required
              />
              <TextField
                label="Currency"
                name="currency"
                value={form.currency}
                onChange={handleAmountCurrencyChange}
                fullWidth
                margin="normal"
                required
              />
              <TextField
                label="Description"
                name="description"
                value={form.description}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleModalClose}>Cancel</Button>
              <Button type="submit" variant="contained">
                {editingId ? "Update" : "Add"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
        {/* Delete Confirm Dialog */}
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this config?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button color="error" onClick={handleDelete}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
        <GlobalToast toast={toast} />
      </Box>
    </>
  );
};

export default AdminPrices;
